class Point {
    constructor(x, y, initialOpacity, minOpacity, maxOpacity, recoveringSpeed) {
        this.x = x;
        this.y = y;
        this.opacity = initialOpacity;
        this.opacityChange = 0;
        this.recovering = false;
        this.minOpacity = minOpacity;
        this.maxOpacity = maxOpacity;
        this.recoveringSpeed = recoveringSpeed;
    }

    update() {
        this.opacity += this.opacityChange;
        this.opacity = Math.min(Math.max(this.opacity, this.minOpacity), this.maxOpacity);

        if (!this.recovering && this.opacity <= this.minOpacity) {
            this.recovering = true;
            this.opacityChange = this.recoveringSpeed;
        } else if (this.recovering && this.opacity >= this.maxOpacity) {
            this.recovering = false;
            this.opacityChange = 0;
        }

        if (Math.abs(this.opacityChange) > 0.001) {
            this.opacityChange *= 0.45;
        }
    }
}

class MovingPoint {
    constructor(canvasWidth, canvasHeight, radius, color, minOpacity, maxOpacity, minSpeed, maxSpeed, numPoints, numRows, movingPoints) {
        this.canvasWidth = canvasWidth;
        this.canvasHeight = canvasHeight;
        this.radius = radius;
        this.color = color;
        this.minOpacity = minOpacity;
        this.maxOpacity = maxOpacity;
        this.speed = (Math.random() * (maxSpeed - minSpeed) + minSpeed);
        this.numPoints = numPoints;
        this.numRows = numRows;
        this.movingPoints = movingPoints;

        this.opacity = Math.random() * (maxOpacity - minOpacity) + minOpacity;
        this.spacing = this.canvasWidth / this.numPoints;

        this.chooseNewCoordinates();
    }

    checkIfLineOccupied(index, direction) {
        if (!this.movingPoints) return false;

        for (const point of this.movingPoints) {
            if (point.lineIndex === index && point.direction === direction) {
                return true;
            }
        }
        return false;
    }

    chooseNewCoordinates() {
        this.direction = Math.random() < 0.5 ? 'horizontal' : 'vertical';
        let lineIndex;
        let attempts = 10;

        do {
            if (this.direction === 'horizontal') {
                lineIndex = Math.floor(Math.random() * this.numRows);
            } else {
                lineIndex = Math.floor(Math.random() * this.numPoints);
            }
            attempts--;
        } while (this.checkIfLineOccupied(lineIndex, this.direction) && attempts > 0);

        this.lineIndex = lineIndex;
        const coordinate = this.lineIndex * this.spacing;

        if (this.direction === 'horizontal') {
            this.x = -this.radius;
            this.y = coordinate;
        } else {
            this.x = coordinate;
            this.y = -this.radius;
        }
    }

    update() {
        if (this.direction === 'horizontal') {
            this.x += this.speed;
            if (this.x > this.canvasWidth + this.radius) {
                this.chooseNewCoordinates();
            }
        } else {
            this.y += this.speed;
            if (this.y > this.canvasHeight + this.radius) {
                this.chooseNewCoordinates();
            }
        }
    }

    draw(ctx) {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, 2 * Math.PI);
        ctx.fillStyle = `rgba(${this.color}, ${this.opacity})`;
        ctx.fill();
    }
}

class MovingArrow {
    constructor(canvasWidth, canvasHeight, arrowSize, color, minOpacity, maxOpacity, minSpeed, maxSpeed, numPoints, movingArrows) {
        this.canvasWidth = canvasWidth;
        this.canvasHeight = canvasHeight;
        this.arrowSize = arrowSize;
        this.color = color;
        this.minOpacity = minOpacity;
        this.maxOpacity = maxOpacity;
        this.speed = Math.random() * (maxSpeed - minSpeed) + minSpeed;
        this.numPoints = numPoints;
        this.spacing = this.canvasWidth / this.numPoints;
        this.movingArrows = movingArrows;

        this.opacity = Math.random() * (maxOpacity - minOpacity) + minOpacity;

        this.chooseNewCoordinates();
    }

    checkIfLineOccupied(index) {
        if (!this.movingArrows) return false;

        for (const arrow of this.movingArrows) {
            if (arrow.lineIndex === index) {
                return true;
            }
        }
        return false;
    }

    chooseNewCoordinates() {
        let attempts = 10;
        do {
            this.lineIndex = Math.floor(Math.random() * this.numPoints);
            attempts--;
        } while (this.checkIfLineOccupied(this.lineIndex) && attempts > 0);

        const coordinate = this.lineIndex * this.spacing;

        this.x = coordinate;
        this.y = this.canvasHeight + this.arrowSize;
    }

    update() {
        this.y -= this.speed;
        if (this.y < -this.arrowSize) {
            this.chooseNewCoordinates();
        }
    }

    draw(ctx) {
        ctx.beginPath();
        ctx.moveTo(this.x, this.y);
        ctx.lineTo(this.x - this.arrowSize / 2.5, this.y + this.arrowSize / 2);
        ctx.lineTo(this.x + this.arrowSize / 2.5, this.y + this.arrowSize / 2);
        ctx.closePath();

        ctx.moveTo(this.x, this.y + this.arrowSize / 2);
        ctx.lineTo(this.x - this.arrowSize / 2.5, this.y + this.arrowSize);
        ctx.lineTo(this.x + this.arrowSize / 2.5, this.y + this.arrowSize);
        ctx.closePath();

        ctx.fillStyle = `rgba(${this.color}, ${this.opacity})`;
        ctx.fill();
    }
}

class GrowthHeroGrid {
    constructor(el) {
        this.el = el;
        this.canvas = null;
        this.ctx = null;
        this.points = [];
        this.movingPoints = [];
        this.movingArrows = [];
        this.resizeTimeout = null;

        // Configuration variables
        this.basicWidth = 1920;
        this.numPoints = this.getNumPoints();
        // movingPointRadius on mobile is 2, on tablet is 3, on desktop is 4
        this.pointRadius = this.getPointRadius();
        this.pointColor = '242, 242, 242';
        this.lineColor = '242, 242, 242';
        this.initialOpacity = 1;
        this.minOpacity = 1;
        this.maxOpacity = this.initialOpacity;
        this.recoveringSpeed = 0.002;
        this.animationSpeed = 75;
        this.opacityPropagationSpeed = 0.75;
        this.triggerInterval = 55;
        this.resizeDelay = 200;

        // Moving points configuration
        this.movingPointsCount = 5;
        // movingPointRadius on mobile is 3, on tablet is 4, on desktop is 6
        this.movingPointRadius = this.getMovingPointRadius();
        this.movingPointColors = [
            '147, 202, 21',
            '167, 217, 21',
            '187, 232, 21',
        ];
        this.movingPointMinOpacity = 1;
        this.movingPointMaxOpacity = 1;
        this.movingPointMinSpeed = 0.2;
        this.movingPointMaxSpeed = 1.0;

        // Moving arrows configuration
        this.movingArrowsCount = 5;
        // arrowSize on mobile is 10, on tablet is 16, on desktop is 22
        this.arrowSize = this.getArrowSize();
        this.arrowColors = [
            '100, 100, 100',
            '60, 60, 60',
            '40, 40, 40',
        ];
        this.arrowMinOpacity = 1;
        this.arrowMaxOpacity = 1;
        this.arrowMinSpeed = 0.2;
        this.arrowMaxSpeed = 1.0;

        this.init();
        this.draw();
    }

    getPointRadius() {
        if (window.innerWidth < 768) {
            return 2;
        } else if (window.innerWidth < 1024) {
            return 3;
        } else {
            return 4;
        }
    }

    getArrowSize() {
        if (window.innerWidth < 768) {
            return 12;
        } else if (window.innerWidth < 1024) {
            return 18;
        } else {
            return 22;
        }
    }

    getMovingPointRadius() {
        if (window.innerWidth < 768) {
            return 4;
        } else if (window.innerWidth < 1024) {
            return 5;
        } else {
            return 6;
        }
    }

    getRandomItemFromArray(array) {
        return array[Math.floor(Math.random() * array.length)];
    }

    init() {
        this.canvas = this.el.querySelector('canvas');

        this.ctx = this.canvas.getContext('2d');

        this.setDimensions();

        this.numRows = Math.floor(window.innerHeight / (this.canvas.width / this.numPoints)); // Add this line

        this.createPoints();
        this.createMovingPoints();
        this.createMovingArrows();

        setInterval(() => {
            const randomX = Math.floor(Math.random() * this.numPoints);
            const randomY = Math.floor(Math.random() * this.numRows);
            this.propagateOpacityChange(randomX, randomY, -0.05);
        }, this.triggerInterval);

        window.addEventListener('resize', this.handleResize.bind(this));
    }

    handleResize() {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
            this.setDimensions();
            this.numPoints = this.getNumPoints();
            this.numRows = Math.ceil(window.innerHeight / (this.canvas.width / this.numPoints)); // Add this line
            this.destroy();
            this.init();
        }, this.resizeDelay);
    }

    setDimensions() {
        let parent = this.canvas.parentElement;
        if (!parent) {
            console.error("No parent element found for canvas");
            return;
        }

        this.canvas.width = parent.offsetWidth;
        this.canvas.height = parent.offsetHeight;
    }

    getNumPoints() {
        if (window.innerWidth < 768) {
            return 10;
        } else if (window.innerWidth < 1024) {
            return 15;
        } else {
            return 25;
        }
    }

    destroy() {
        this.points.length = 0;
        this.movingPoints.length = 0;
        this.movingArrows.length = 0;

        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        window.removeEventListener('resize', this.handleResize);
    }

    createPoints() {
        const spacing = this.canvas.width / this.numPoints;

        for (let i = 0; i <= this.numPoints; i++) {
            const x = i * spacing;
            const row = [];
            for (let j = 0; j <= this.numRows; j++) {
                const y = j * spacing;
                row.push(new Point(x, y, this.initialOpacity, this.minOpacity, this.maxOpacity, this.recoveringSpeed));
            }
            this.points.push(row);
        }
    }

    createMovingPoints() {
        for (let i = 0; i < this.movingPointsCount; i++) {
            this.movingPoints.push(
                new MovingPoint(
                    this.canvas.width,
                    this.canvas.height,
                    this.movingPointRadius,
                    this.getRandomItemFromArray(this.movingPointColors),
                    this.movingPointMinOpacity,
                    this.movingPointMaxOpacity,
                    this.movingPointMaxSpeed,
                    this.movingPointMinSpeed,
                    this.numPoints,
                    this.numRows,
                    this.movingPoints
                )
            );
        }
    }

    createMovingArrows() {
        for (let i = 0; i < this.movingArrowsCount; i++) {
            this.movingArrows.push(
                new MovingArrow(
                    this.canvas.width,
                    this.canvas.height,
                    this.arrowSize,
                    this.getRandomItemFromArray(this.arrowColors),
                    this.arrowMinOpacity,
                    this.arrowMaxOpacity,
                    this.arrowMinSpeed,
                    this.arrowMaxSpeed,
                    this.numPoints,
                    this.movingArrows
                )
            );
        }
    }

    propagateOpacityChange(x, y, change) {
        if (x < 0 || x >= this.numPoints || y < 0 || y >= this.numRows) {
            return;
        }

        const point = this.points[x][y];

        if (point && !point.recovering) {
            if (Math.abs(point.opacityChange) < Math.abs(change)) {
                point.opacityChange = change;

                setTimeout(() => {
                    this.propagateOpacityChange(x - 1, y, change * this.opacityPropagationSpeed);
                    this.propagateOpacityChange(x + 1, y, change * this.opacityPropagationSpeed);
                    this.propagateOpacityChange(x, y - 1, change * this.opacityPropagationSpeed);
                    this.propagateOpacityChange(x, y + 1, change * this.opacityPropagationSpeed);
                }, this.animationSpeed);
            }
        }
    }

    draw() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        for (let i = 0; i <= this.numPoints; i++) {
            for (let j = 0; j <= this.numRows; j++) {
                const point = this.points[i][j];

                if (i < this.numPoints) {
                    const right = this.points[i + 1][j];
                    const opacity = (point.opacity + right.opacity) / 2;
                    this.ctx.beginPath();
                    this.ctx.moveTo(point.x, point.y);
                    this.ctx.lineTo(right.x, right.y);
                    this.ctx.strokeStyle = `rgba(${this.lineColor}, ${opacity})`;
                    this.ctx.stroke();
                }

                if (j < this.numRows) {
                    const down = this.points[i][j + 1];
                    const opacity2 = (point.opacity + down.opacity) / 2;
                    this.ctx.beginPath();
                    this.ctx.moveTo(point.x, point.y);
                    this.ctx.lineTo(down.x, down.y);
                    this.ctx.strokeStyle = `rgba(${this.lineColor}, ${opacity2})`;
                    this.ctx.stroke();
                }
            }
        }

        this.points.forEach(row => {
            row.forEach(point => {
                point.update();

                this.ctx.beginPath();
                this.ctx.arc(point.x, point.y, this.pointRadius, 0, 2 * Math.PI);
                this.ctx.fillStyle = `rgba(${this.pointColor}, ${point.opacity})`;
                this.ctx.fill();
            });
        });

        this.movingPoints.forEach(point => {
            point.update();
            point.draw(this.ctx);
        });

        this.movingArrows.forEach(arrow => {
            arrow.update();
            arrow.draw(this.ctx);
        });

        requestAnimationFrame(this.draw.bind(this));
    }
}

export default GrowthHeroGrid
